import { t } from '@lingui/core/macro';
import { useMemo, useState } from 'react';

import { useAdGroups } from '@/api/ad';
import {
  encodeFilterFieldName,
  FilterField,
  InfiniteComboboxPickerFilter,
  InfiniteScrollCategoryFilter,
  useFilterForm,
} from '@/components/filters';
import type { FilterOperatorMulti } from '@/components/InstantSearch';
import type { FilterComponentProps } from '@/providers/TableState';

const PAGE_SIZE = 50;

type Props = FilterComponentProps & {
  mode?: 'multiple' | 'single';
  hasExclude?: boolean;
  channelId?: string;
  placeholder?: string;
  notFoundContent?: string;
};

const AdGroupFilter = ({
  name,
  label,
  infoPopover,
  compact = false,
  mode = 'multiple',
  hasExclude = true,
  channelId,
  placeholder,
  notFoundContent,
  ...props
}: Props) => {
  const form = useFilterForm();
  const [search, setSearch] = useState<string>('');

  const { adGroupOptions, error, hasMore, isLoading, isFetchingNextPage, fetchNextPage } =
    useAdGroups({
      search,
      pageSize: PAGE_SIZE,
      channelId,
    });

  const defaultPlaceholder = useMemo(
    () => (mode === 'single' ? t`Select Ad Group...` : t`Select Ad Groups...`),
    [mode],
  );
  const finalPlaceholder = placeholder ? placeholder : defaultPlaceholder;

  return (
    <form.AppField name={encodeFilterFieldName(name)}>
      {(field) => (
        <FilterField
          data-testid="ad-group-filter"
          label={label}
          infoPopover={infoPopover}
          hideLabel={!compact}
        >
          {compact ? (
            <InfiniteComboboxPickerFilter
              value={field.state.value as FilterOperatorMulti}
              onChange={field.handleChange}
              mode={mode}
              placeholder={finalPlaceholder}
              loadingPlaceholder={t`Loading Ad Groups...`}
              options={adGroupOptions}
              isLoading={isLoading}
              isLoadingMore={isFetchingNextPage}
              hasMore={hasMore}
              error={error}
              loadMore={fetchNextPage}
              notFoundContent={notFoundContent || t`No ad groups were found...`}
              hasExclude={hasExclude}
              onSearch={setSearch}
            />
          ) : (
            <InfiniteScrollCategoryFilter
              {...props}
              value={field.state.value as FilterOperatorMulti}
              onChange={field.handleChange}
              selectionMode={mode}
              options={adGroupOptions}
              isLoading={isLoading}
              isLoadingMore={isFetchingNextPage}
              hasMore={hasMore}
              error={error}
              loadMore={fetchNextPage}
              searchText={search}
              onSearch={setSearch}
            />
          )}
        </FilterField>
      )}
    </form.AppField>
  );
};

export default AdGroupFilter;
